import React from 'react';
import {
  Typography, TextField, Paper, Select, MenuItem, FormControl, InputLabel, Switch, FormControlLabel, Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import moment from 'moment';
import 'moment/locale/fr';
import 'moment/locale/en-gb';
import {
  getCustomFieldType, convertValueFromStorage, convertValueForStorage, getFormatConfig,
} from 'components/admin/inventory/mapping/customFieldUtils';

/**
 * @param {object} props
 * @param {any} props.mappingConfig
 * @param {any} props.formik
 * @param {any} props.itemData
 */
function CustomOperatorFieldsInput({
  mappingConfig,
  formik,
  itemData,
}) {
  const { t } = useTranslation();
  const currentLocale = t('locale');
  const momentLocale = currentLocale === 'fr' ? 'fr' : 'en-gb';

  React.useEffect(() => {
    moment.locale(momentLocale);
  }, [momentLocale]);

  // Extract custom fields from the new unified config system
  const mappingConfigFields = mappingConfig && 'customFields' in mappingConfig
    ? mappingConfig.customFields : null;
  const customOperatorFields = mappingConfigFields?.map((/** @type {string} */ fieldId) => {
    const columns = (mappingConfig && 'columns' in mappingConfig ? mappingConfig.columns : null);
    const fieldConfig = columns?.[fieldId];
    const result = /** @type {{type: string, format: string, listItems: string[]}} */ (getCustomFieldType(fieldConfig));
    const { type, format, listItems } = result;
    return {
      id: fieldId,
      csvName: fieldConfig?.mapping?.csvName || fieldId,
      type,
      format,
      listItems,
      displayStyle: fieldConfig?.displayStyle || 'inputField',
    };
  }) || [];

  if (!customOperatorFields || customOperatorFields.length === 0) {
    return null;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        px: { xs: 1, md: 3 },
        py: 2,
        border: 1,
        borderColor: 'grey.400',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        pt: 5,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 'bold',
          color: 'grey.600',
          position: 'absolute',
          top: 2,
          right: 10,
        }}
      >
        {t('create_inventory.custom_operator_fields')}
      </Typography>

      {customOperatorFields.map((/** @type {any} */ field) => {
        // Get the current value from the container data
        const storedValue = itemData[field.csvName];
        const formatConfig = getFormatConfig(field.type, field.format);

        // Get current value from formik or itemData
        const formikValues = (formik && 'values' in formik ? formik.values : {});
        const formValue = formikValues[field.id]?.value;
        let currentValue;

        if (formValue !== undefined) {
          currentValue = formValue;
        } else if (field.type === 'date') {
          // For dates: keep ISO from storage for DatePicker
          currentValue = storedValue;
        } else {
          // For other types: convert from storage format to display format
          currentValue = convertValueFromStorage(storedValue, field.type, field.format);
        }

        // Unit is fixed from format config, not from formik
        // For lists, use "enum" instead of "custom" for better clarity
        let unit;
        if (field.type === 'list') {
          unit = 'enum';
        } else if (formatConfig && 'symbol' in formatConfig) {
          unit = formatConfig.symbol;
        } else {
          unit = field.format;
        }

        const handleValueChange = (/** @type {any} */ newValue) => {
          // For dates, newValue is already ISO from DatePicker
          // For other types, convert to storage format
          let convertedValue;
          if (field.type === 'date') {
            convertedValue = newValue; // Already ISO
          } else {
            convertedValue = convertValueForStorage(newValue, field.type, field.format);
          }
          if (formik && 'setFieldValue' in formik) {
            formik.setFieldValue(field.id, { value: convertedValue, unit });
          }
        };

        // Render appropriate input based on type
        let inputComponent;

        switch (field.type) {
          case 'text': {
            const isMultiline = Boolean(formatConfig && 'multiline' in formatConfig ? formatConfig.multiline : false);
            inputComponent = (
              <TextField
                key={field.id}
                id={field.id}
                name={field.id}
                label={field.csvName}
                value={currentValue || ''}
                onChange={(event) => handleValueChange(event.target.value)}
                onBlur={formik && 'handleBlur' in formik ? formik.handleBlur : undefined}
                error={(() => {
                  const touched = formik && 'touched' in formik ? formik.touched[field.id] : false;
                  const errors = formik && 'errors' in formik ? formik.errors[field.id] : null;
                  return touched && Boolean(errors);
                })()}
                helperText={(() => {
                  const touched = formik && 'touched' in formik ? formik.touched[field.id] : false;
                  const errors = formik && 'errors' in formik ? formik.errors[field.id] : null;
                  return touched && errors;
                })()}
                fullWidth
                variant="outlined"
                size="small"
                multiline={isMultiline}
                rows={isMultiline ? 4 : 1}
              />
            );
            break;
          }
          case 'volume':
          case 'mass': {
            // Get unit symbol from format config
            const unitSymbol = (formatConfig && 'symbol' in formatConfig ? formatConfig.symbol : null) || field.format;
            inputComponent = (
              <Box key={field.id} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  id={field.id}
                  name={field.id}
                  label={field.csvName}
                  type="number"
                  value={currentValue || ''}
                  onChange={(event) => {
                    const val = event.target.value;
                    // Only allow numbers
                    if (val === '' || !isNaN(Number(val))) {
                      handleValueChange(val);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.touched[field.id] && Boolean(formik.errors[field.id])}
                  helperText={formik.touched[field.id] && formik.errors[field.id]}
                  sx={{ flex: 1 }}
                  variant="outlined"
                  size="small"
                  inputProps={{ step: 0.01 }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    minWidth: 80,
                    px: 1.5,
                    py: 1,
                    border: 1,
                    borderColor: 'grey.300',
                    borderRadius: 1,
                    bgcolor: 'grey.50',
                    fontSize: '0.875rem',
                    color: 'text.secondary',
                  }}
                >
                  {unitSymbol}
                </Box>
              </Box>
            );
            break;
          }
          case 'list': {
            const hasCurrentValue = currentValue !== undefined && currentValue !== null && currentValue !== '';
            const isUnknownListValue = hasCurrentValue && !field.listItems.includes(currentValue);
            const touched = formik && 'touched' in formik ? formik.touched[field.id] : false;
            const errors = formik && 'errors' in formik ? formik.errors[field.id] : null;
            const helperText = isUnknownListValue
              ? t('operation.custom_list_value_not_in_options', {
                defaultValue: 'This value is not in the configured list.',
              })
              : touched && errors;

            inputComponent = (
              <FormControl
                key={field.id}
                fullWidth
                size="small"
                error={isUnknownListValue || (touched && Boolean(errors))}
              >
                <InputLabel id={`${field.id}-label`}>{field.csvName}</InputLabel>
                <Select
                  labelId={`${field.id}-label`}
                  id={field.id}
                  name={field.id}
                  value={hasCurrentValue ? currentValue : ''}
                  label={field.csvName}
                  onChange={(event) => handleValueChange(event.target.value)}
                  onBlur={formik && 'handleBlur' in formik ? formik.handleBlur : undefined}
                >
                  {isUnknownListValue && (
                    <MenuItem disabled value={currentValue} sx={{ color: 'error.main' }}>
                      {currentValue}
                    </MenuItem>
                  )}
                  {field.listItems.map((/** @type {string} */ item) => (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </Select>
                {helperText && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {helperText}
                  </Typography>
                )}
              </FormControl>
            );
            break;
          }
          case 'date': {
            const isDateTime = (formatConfig && 'withTime' in formatConfig ? formatConfig.withTime : false) || false;
            const patternFromConfig = formatConfig && 'pattern' in formatConfig
              ? formatConfig.pattern : null;
            const datePattern = patternFromConfig || 'DD/MM/YYYY';

            // Convert ISO string to moment object for DatePicker
            // currentValue should be ISO string (from storage or formik)
            let momentValue = null;
            if (currentValue) {
              if (typeof currentValue === 'string' && currentValue.includes('T')) {
                // ISO format
                momentValue = moment(currentValue);
              } else if (typeof currentValue === 'string') {
                // Fallback: try parsing as display format
                // (shouldn't happen if getDefaultFormValues is correct)
                const patternStr = String(datePattern);
                momentValue = moment(currentValue, patternStr, true);
              } else {
                momentValue = moment(currentValue);
              }
              if (!momentValue || !momentValue.isValid()) {
                momentValue = null;
              }
            }

            const handleDateChange = (/** @type {any} */ newValue) => {
              if (newValue && moment(newValue).isValid()) {
                // Convert to ISO for storage
                const isoString = moment(newValue).toISOString();
                handleValueChange(isoString);
              } else {
                handleValueChange('');
              }
            };

            inputComponent = (
              <LocalizationProvider
                key={field.id}
                dateAdapter={AdapterMoment}
                adapterLocale={momentLocale}
              >
                {isDateTime ? (
                  <DateTimePicker
                    label={field.csvName}
                    value={momentValue}
                    onChange={handleDateChange}
                    format={String(datePattern)}
                    slotProps={{
                      textField: {
                        id: field.id,
                        name: field.id,
                        error: (() => {
                          const touched = formik && 'touched' in formik ? formik.touched[field.id] : false;
                          const errors = formik && 'errors' in formik ? formik.errors[field.id] : null;
                          return touched && Boolean(errors);
                        })(),
                        helperText: (() => {
                          const touched = formik && 'touched' in formik ? formik.touched[field.id] : false;
                          const errors = formik && 'errors' in formik ? formik.errors[field.id] : null;
                          return touched && errors;
                        })(),
                        fullWidth: true,
                        variant: 'outlined',
                        size: 'small',
                        onBlur: formik && 'handleBlur' in formik ? formik.handleBlur : undefined,
                      },
                    }}
                  />
                ) : (
                  <DesktopDatePicker
                    label={field.csvName}
                    value={momentValue}
                    onChange={handleDateChange}
                    format={String(datePattern)}
                    slotProps={{
                      textField: {
                        id: field.id,
                        name: field.id,
                        error: (() => {
                          const touched = formik && 'touched' in formik ? formik.touched[field.id] : false;
                          const errors = formik && 'errors' in formik ? formik.errors[field.id] : null;
                          return touched && Boolean(errors);
                        })(),
                        helperText: (() => {
                          const touched = formik && 'touched' in formik ? formik.touched[field.id] : false;
                          const errors = formik && 'errors' in formik ? formik.errors[field.id] : null;
                          return touched && errors;
                        })(),
                        fullWidth: true,
                        variant: 'outlined',
                        size: 'small',
                        onBlur: formik && 'handleBlur' in formik ? formik.handleBlur : undefined,
                      },
                    }}
                  />
                )}
              </LocalizationProvider>
            );
            break;
          }
          case 'number': {
            const isInteger = (formatConfig && 'decimal' in formatConfig ? formatConfig.decimal === false : false);
            inputComponent = (
              <TextField
                key={field.id}
                id={field.id}
                name={field.id}
                label={field.csvName}
                type="number"
                value={currentValue || ''}
                onChange={(event) => {
                  const val = event.target.value;
                  // Only allow numbers
                  if (val === '' || !isNaN(Number(val))) {
                    handleValueChange(val);
                  }
                }}
                onBlur={formik && 'handleBlur' in formik ? formik.handleBlur : undefined}
                error={(() => {
                  const touched = formik && 'touched' in formik ? formik.touched[field.id] : false;
                  const errors = formik && 'errors' in formik ? formik.errors[field.id] : null;
                  return touched && Boolean(errors);
                })()}
                helperText={(() => {
                  const touched = formik && 'touched' in formik ? formik.touched[field.id] : false;
                  const errors = formik && 'errors' in formik ? formik.errors[field.id] : null;
                  return touched && errors;
                })()}
                fullWidth
                variant="outlined"
                size="small"
                inputProps={{ step: isInteger ? 1 : 0.01 }}
              />
            );
            break;
          }
          case 'boolean': {
            const trueLabel = (formatConfig && 'trueLabel' in formatConfig ? formatConfig.trueLabel : null) || 'Oui';
            const falseLabel = (formatConfig && 'falseLabel' in formatConfig ? formatConfig.falseLabel : null) || 'Non';
            const checkedValue = convertValueForStorage(currentValue, field.type, field.format) === true;
            inputComponent = (
              <FormControlLabel
                key={field.id}
                control={(
                  <Switch
                    id={field.id}
                    name={field.id}
                    checked={checkedValue}
                    onChange={(event) => handleValueChange(event.target.checked)}
                    onBlur={formik.handleBlur}
                  />
                )}
                label={`${field.csvName}: ${checkedValue ? trueLabel : falseLabel}`}
              />
            );
            break;
          }
          default: {
            // Fallback to text field
            inputComponent = (
              <TextField
                key={field.id}
                id={field.id}
                name={field.id}
                label={field.csvName}
                value={currentValue || ''}
                onChange={(event) => handleValueChange(event.target.value)}
                onBlur={formik && 'handleBlur' in formik ? formik.handleBlur : undefined}
                error={(() => {
                  const touched = formik && 'touched' in formik ? formik.touched[field.id] : false;
                  const errors = formik && 'errors' in formik ? formik.errors[field.id] : null;
                  return touched && Boolean(errors);
                })()}
                helperText={(() => {
                  const touched = formik && 'touched' in formik ? formik.touched[field.id] : false;
                  const errors = formik && 'errors' in formik ? formik.errors[field.id] : null;
                  return touched && errors;
                })()}
                fullWidth
                variant="outlined"
                size="small"
              />
            );
          }
        }

        return inputComponent;
      })}
    </Paper>
  );
}

export default CustomOperatorFieldsInput;
